import { useEffect, useRef, useMemo } from 'react';
import { useProjectsStore } from '@/stores/projects';
import { useDebounce } from '@/hooks/use-debounce';
import { useWorkspaceStore } from '@/stores/workspace';
import { getLanguageFromPath } from "@/stores/workspace";
import { useInstructionsStore } from '@/stores/instructions';
import { FileTab } from '@/types';

export const useWorkspaceState = () => {
  // Centralized workspace store
  const workspaceState = useWorkspaceStore((state) => ({
    instructions: state.instructions,
    userSelectedFiles: state.userSelectedFiles,
    includeFileTree: state.includeFileTree,
    expandedFolders: state.expandedFolders,
    openFilePath: state.openFilePath,
    isAutoContextEnabled: state.isAutoContextEnabled,
    tokenBudget: state.tokenBudget,
    tabs: state.tabs ?? [],
    selectedTabIds: state.selectedTabIds ?? [],
    groups: state.groups ?? []
  }));
  const {
    instructions,
    userSelectedFiles,
    includeFileTree,
    expandedFolders,
    openFilePath,
    tabs,
    selectedTabIds,
    groups
  } = workspaceState;

  // Store references
  const { activeProjectId, byId: projectsById, updateProject } = useProjectsStore();
  const activeProject = activeProjectId ? projectsById[activeProjectId] : null;

  // State for managing hydration logic
  const isStateLoadedRef = useRef(false);

  // State persistence
  const workspaceStateToPersist = useMemo(() => ({
    selectedFiles: Array.from(userSelectedFiles),
    instructions,
    includeFileTree,
    expandedFolders: Array.from(expandedFolders),
    openFilePath,
    tabs: tabs,
    selectedTabIds: selectedTabIds,
    groups: groups
  }), [userSelectedFiles, instructions, includeFileTree, expandedFolders, openFilePath, tabs]);

  const debouncedStateToPersist = useDebounce(workspaceStateToPersist, 1000);
  const lastSavedStateRef = useRef<string>();
  const prevProjectIdRef = useRef<string | null>(null);

  // Persist state to project
  useEffect(() => {
    if (activeProjectId && isStateLoadedRef.current) {
      const stateString = JSON.stringify(debouncedStateToPersist);
      if (stateString !== lastSavedStateRef.current) {
        updateProject(activeProjectId, { workspaceState: debouncedStateToPersist });
        lastSavedStateRef.current = stateString;
      }
    }
  }, [debouncedStateToPersist, activeProjectId, updateProject]);

  // Persist user settings to localStorage
  useEffect(() => {
    try {
      const userSettings = JSON.stringify({ isAutoContextEnabled: workspaceState.isAutoContextEnabled, tokenBudget: workspaceState.tokenBudget });
      window.localStorage.setItem('workspace-user-settings', userSettings);
    } catch (error) {
      console.warn('Error saving workspace user settings to localStorage', error);
    }
  }, [workspaceState.isAutoContextEnabled, workspaceState.tokenBudget]);


  // Hydrate state from project only when project ID changes
  useEffect(() => {
      if (!activeProject || prevProjectIdRef.current === activeProject.id) return;
      const ws = activeProject.workspaceState as any;

      let lastInstructionsUpdate = useInstructionsStore.getState().lastUpdated;

      let instructions = ws?.instructions || '';
      if (lastInstructionsUpdate + 5000 > Date.now() && lastInstructionsUpdate !== 0) {
        instructions = useInstructionsStore.getState().instructions;
      }
      const hydratedState = {
        userSelectedFiles: new Set<string>(ws?.selectedFiles || []),
        includeFileTree: ws?.includeFileTree || false,
        expandedFolders: new Set<string>(ws?.expandedFolders || []),
        openFilePath: ws?.openFilePath,
        instructions: instructions,
        tabs: ws?.tabs ?? [],
        selectedTabIds: ws?.selectedTabIds || [],
        groups: ws?.groups || []
      };
      useWorkspaceStore.setState(hydratedState);
      useInstructionsStore.getState().setInstructions(instructions || '');

      const newPersistedState = {
        selectedFiles: Array.from(hydratedState.userSelectedFiles),
        instructions: hydratedState.instructions,
        includeFileTree: hydratedState.includeFileTree,
        expandedFolders: Array.from(hydratedState.expandedFolders),
        openFilePath: hydratedState.openFilePath,
        tabs: hydratedState.tabs,
        selectedTabIds: hydratedState.selectedTabIds,
        groups: hydratedState.groups
      };

      // After hydrating, update the last saved state ref to prevent immediate re-saving.
      lastSavedStateRef.current = JSON.stringify(newPersistedState);

      isStateLoadedRef.current = true;
      prevProjectIdRef.current = activeProject.id;

  }, [activeProject?.id, activeProject?.workspaceState]);

  return { isStateLoaded: isStateLoadedRef.current };
};